import React from 'react';
import {
    Button,
    FormText,
    Progress,
} from 'reactstrap';
import { faTasks, faDownload, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../Common';
import {
    getFilterModel,
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    PageHeading,
    PageWrap,
    toasty,
    onFieldChange,
    FlexRow,
    Textarea,
} from '../common/forms/FormElements';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import { handleFormSaveError } from '../common/forms/ValidationError';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { NotAuthorizedPage } from '../status/StatusCodes';
import FormErrorResponseDisplay from '../common/forms/FormErrorResponseDisplay';
import { Modal } from '../common/Modal';

// Used to load from cache for the list types.
const getInitialListFiltersFromCache = (initialFilters) => {
    const json = sessionStorage.getItem('billing_gridFilterSettings');

    if (json) {
        const obj = JSON.parse(json);

        initialFilters.dispatchId = obj.DispatchLocationId
            ? obj.DispatchLocationId.value
            : '';
        initialFilters.hasOverride = obj.HasOverride
            ? obj.HasOverride.value
            : '';
        initialFilters.companyId = obj.CustomerCompanyId
            ? parseInt(obj.CustomerCompanyId.value)
            : 0;
        initialFilters.operationsCenterId = obj.OperationsCenterId
            ? parseInt(obj.OperationsCenterId.value)
            : 0;
        initialFilters.billingStatusId = obj.BillingStatusId
            ? parseInt(obj.BillingStatusId.value)
            : 0;
        initialFilters.payrollStatusId = obj[
            'Timesheet.PayrollStatus.Description'
        ]
            ? parseInt(obj['Timesheet.PayrollStatus.Description'].value)
            : 0;
    }

    return initialFilters;
};

const printTimesheet = (id) => {
    if (id) {
        httpClient.open(ApiRoutes.report.timesheetBilling(id), '_self');
    }
};

class BillingIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.timesheetDetailsRef = React.createRef();
        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            timesheetDetailsOpen: false,
            selectedDetails: [],
            showRejectTimesheetModal: false,
            isRejectingTimesheet: false,
            adminRejecting: false,
            selectedRowRejectionNotes: '',
            loadingReport: false,
            yesNo,
            isApproving: false,
            errorResponse: {},
        };

        this.onRowClicked = this.onRowClicked.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onRowSelected = this.onRowSelected.bind(this);
        this.toggleRejectTimesheetModal = this.toggleRejectTimesheetModal.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onAcceptButtonClick = async () => {
        this.setState({ isApproving: true });

        const { rowsSelected } = this.state;

        // call accept
        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.id);
            const response = await httpClient
                .post(ApiRoutes.billing.approve(), model)
                .catch(this.handleSaveError);

            if (response) {
                toasty.error('Error approving billing records');
                this.displaySaveErrors({ title: 'Error approving billing records', errors: [response] });
            } else {
                toasty.success('Billing records approved');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({ rowsSelected: [], isApproving: false });
    };

    onChange = onFieldChange;

    async onExportClick() {
        // this.setState({ loadingReport: true });
        const { gridOptions } = this.state;
        let model = {};
        if (gridOptions.api.isAnyFilterPresent()) model = getFilterModel(gridOptions.api.getFilterModel());
        try {
            const response = await httpClient.post(
                ApiRoutes.billing.export(),
                model,
            );
            if (response) {
                toasty.error(response);
            } else {
                toasty.success('Billing Report complete');
            }
        } catch {
            toasty.error('Error exporting billing');
        }
        // this.setState({ loadingReport: false });
        this.state.gridOptions.refresh();
    }

    onRowClicked = (event) => {
        const selection = event.api.getSelectedRows();
        const row = selection.length ? selection[0] : null;

        if (row) {
            this.setState({ selectedRow: row });
            // this.openTimesheetDetails();
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    }

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };



    downloadExport = async (id) => {
        if (id) {
            this.setState({ loadingReport: true });
            /* var model = { id: id }; */
            await httpClient
                .downloadFile(
                    ApiRoutes.billing.download(id),
                    null,
                    'Billing.pdf',
                )
                .catch(this.handleSaveError);
            this.setState({ loadingReport: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    openTimesheetDetails = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    populateState = async () => {
        const [dispatchLocations, billingStatuses, payrollStatuses] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
            httpClient.js(ApiRoutes.typeAheads.billingStatuses()),
            httpClient.js(ApiRoutes.typeAheads.payrollStatuses()),
        ]);

        let initialListFilters = {
            initialDispatchId: '',
            initialCustomerCompanyId: 0,
            initialOperationsCenterId: 0,
            initialBillingStatusId: 0,
            initialPayrollStatusId: 0,
            initialHasOverride: null,
        };

        initialListFilters = getInitialListFiltersFromCache(initialListFilters);

        const { tenantSettings } = this.context.tenant;
        const payrollEnabled = !!tenantSettings.payrollEnabled;

        const gridOptions = createGridOptions(this);
        const context = this;

        // https://www.ag-grid.com/documentation/javascript/row-selections#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        gridOptions.rowSelection = 'multiple';

        gridOptions.postProcessData = this.transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;

        gridOptions.rowClassRules = {
            'ag-row-warning': (params) => (params.data ?? {}).hasOverride === 'Yes',
            'ag-row-danger': (params) => (params.data ?? {}).isEmergency === true,
        };

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            iconRenderer: IconCellRenderer,
            variableLinkRenderer: VariableLinkCellRenderer,
        };
        gridOptions.onRowClicked = this.onRowClicked;

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.dispatchId,
        };
        // timesheetStatusFilterParams = {
        //    suppressFilterButton: true,
        //    options: timesheetStatuses,
        //    optionsLabel: 'label',
        //    optionsValue: 'value'
        // },
        const billingStatusFilterParams = {
            suppressFilterButton: true,
            options: billingStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.billingStatusId,
        };
        const payrollStatusFilterParams = {
            suppressFilterButton: true,
            options: payrollStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.payrollStatusId,
        };
        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: this.state.yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.hasOverride,
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
                // floatingFilterComponentParams: {
                //    suppressFilterButton: true,
                // }
            },
            {
                colId: 'customerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'operationsCenterName',
                headerName: 'Operations Center',
                field: 'operationsCenterName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'ContractNumber', // leave this, it doesnt map to an ef prop but is triggered for the sort on the dynamic field.
                headerName: 'Contract #',
                field: 'contractNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'DispatchLocationId',
                headerName: 'Dispatching',
                field: 'dispatchOfficeName',
                sortable: true,
                flex: 2,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'Timesheet.TimesheetNumber',
                headerName: 'Timesheet #',
                field: 'timesheetNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Timesheet.WorkOrderNumber',
                headerName: 'Work Order',
                field: 'workOrderNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'JobLocationAddress',
                headerName: 'Job Address',
                field: 'jobAddress',
                sortable: false,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                headerName: 'Flags',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'BillingStatusId',
                headerName: 'Billing Status',
                field: 'billingStatus',
                sortable: true,
                flex: 1,
                cellRenderer: 'variableLinkRenderer',
                cellRendererParams: (r) => {
                    if ((r.data ?? {}).billingExportId) {
                        return {
                            clicked: this.downloadExport,
                            isLink: true,
                            nameField: 'billingStatus',
                            idField: 'billingExportId',
                            title: 'Download Export',
                        };
                    }
                    return {
                        clicked: this.downloadExport,
                        isLink: false,
                        nameField: 'billingStatus',
                        idField: 'billingExportId',
                        title: 'Download Export',
                    };
                },
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billingStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billingStatusFilterParams,
            },
            {
                colId: 'Timesheet.PayrollStatus.Description',
                headerName: 'Payroll Status',
                field: 'payrollStatus',
                sortable: true,
                flex: 1.5,
                tooltipField: 'status',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: payrollStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: payrollStatusFilterParams,
                hide: !payrollEnabled,
            },
            {
                colId: 'HasOverride',
                headerName: 'Billing Override',
                field: 'hasOverride',
                sortable: true,
                flex: 1.5,
                tooltipField: 'status',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 75,
                headerName: 'View',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: this.openTimesheetDetails,
                    idField: 'timesheetId',
                    iconClass: 'fa-eye',
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 75,
                headerName: 'Print',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: printTimesheet,
                    idField: 'timesheetId',
                    iconClass: 'fa-print',
                },
            },
            {
                colId: 'SelectionPlaceholder',
                headerName: '',
                field: 'selectionPlaceholder',
                sortable: false,
                maxWidth: 75,
                checkboxSelection: true,
            },
        ];

        /// ///////////////////////////////////////////////////////
        // RJP - Cache and reload filter and sort changes
        gridOptions.onFilterChanged = () => {
            const filterModel = gridOptions.api.getFilterModel();
            sessionStorage.setItem(
                'billing_gridFilterSettings',
                JSON.stringify(filterModel),
            );
        };

        gridOptions.onFirstDataRendered = () => {
            const sortModel = sessionStorage.getItem('billing_gridSortSettings');
            if (sortModel) {
                const obj = JSON.parse(sortModel);
                gridOptions.api.setSortModel(obj);
            }
        };

        gridOptions.onGridReady = (params) => {
            const filterModel = sessionStorage.getItem(
                'billing_gridFilterSettings',
            );
            if (filterModel) {
                const obj = JSON.parse(filterModel);

                // Have to set individually here instead of using the whole model
                // due to list types requiring a different initialization routine
                if (obj.ContractNumber) {
                    const filterInstance = gridOptions.api.getFilterInstance('ContractNumber');
                    filterInstance.setModel(obj.ContractNumber);
                    gridOptions.api.onFilterChanged();
                }
                if (obj['Timesheet.TimesheetNumber']) {
                    const filterInstance = gridOptions.api.getFilterInstance(
                        'Timesheet.TimesheetNumber',
                    );
                    filterInstance.setModel(obj['Timesheet.TimesheetNumber']);
                    gridOptions.api.onFilterChanged();
                }
                if (obj.JobLocationAddress) {
                    const filterInstance = gridOptions.api.getFilterInstance('JobLocationAddress');
                    filterInstance.setModel(obj.JobLocationAddress);
                    gridOptions.api.onFilterChanged();
                }
                if (obj.JobDate) {
                    const filterInstance = gridOptions.api.getFilterInstance('JobDate');
                    filterInstance.setModel(obj.JobLocationAddress);
                    gridOptions.api.onFilterChanged();
                }
            }

            params.api.setDatasource(context.state.dataSource);
            context.setState({ gridApi: params.api });
        };

        // END - Cache and reload filter and sort changes
        /// //////////////////////////////////////////////////////

        const dataSource = createDataSource(
            ApiRoutes.billing.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            isRejectingTimesheet: false,
        });
    };

    rejectTimesheet = async ({ selectedRowRejectionNotes }, adminRejecting) => {
        const { rowsSelected } = this.state;

        if (
            selectedRowRejectionNotes.length === 0
            || !selectedRowRejectionNotes.trim()
        ) return;
        this.setState({ isRejectingTimesheet: true });

        // call reject
        if (rowsSelected) {
            const model = {
                billingId: rowsSelected[0].id,
                note: selectedRowRejectionNotes,
            };

            const endPoint = adminRejecting == true
                ? ApiRoutes.billing.adminReject()
                : ApiRoutes.billing.reject();
            const response = await httpClient
                .post(endPoint, model)
                .catch(this.handleSaveError);

            if (response) {
                toasty.success('Billing: Timesheet Rejected');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({
            selectedRow: null,
            showRejectTimesheetModal: false,
            selectedRowRejectionNotes: '',
            isRejectingTimesheet: false,
            adminRejecting: false,
        });
    };

    toggleRejectTimesheetModal = async () => {
        const { rowsSelected } = this.state;

        const adminRequired = await httpClient.get(
            ApiRoutes.billing.needsAdminReject(rowsSelected[0].id),
        );

        // if admin not needed just show regular
        if (adminRequired == false) {
            this.showRejectTimesheetModal(false);
            return;
        }

        // if admin needed and user is not admin
        if (adminRequired == true && !this.context?.user?.isAdmin) {
            toasty.info(
                'An admin needs to reject this timesheet due to it being approved already',
            );
            return;
        }

        // if admin needed and is an admin
        if (adminRequired == true && this.context?.user?.isAdmin) {
            this.showRejectTimesheetModal(true);
        }
    };

    clearSaveErrors = () => this.setState({ errorResponse: {} });

    displaySaveErrors = (response) => this.setState({ errorResponse: response });

    showExportModal = () => {
        Modal.show({
            title: 'Export Billing',
            onSubmit: () => this.onExportClick(),
            SaveButtonProps: {
                label: 'Export',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            body: () => (
                <>
                    Export will be generated based upon
                    search criteria entered on the billing
                    screen. If the result set is too large
                    the export may timeout.
                </>
            ),
        });
    };

    showRejectTimesheetModal = (adminRejecting) => {
        Modal.show({
            title: 'Billing: Reject Timesheet',
            onSubmit: (values) => this.rejectTimesheet(values, adminRejecting),
            SaveButtonProps: {
                label: 'Reject',
                savingLabel: 'Rejecting',
                color: 'danger',
                icon: faUndo,
            },
            body: () => (
                <>
                    {adminRejecting ? (
                        <small
                            style={{ marginBottom: '20px' }}
                            className="form-text text-danger"
                        >
                            This administrative action will
                            delete any approved payroll or
                            billing records, and reset the
                            timesheet. Only administrators will
                            be allowed to adjust and re-approve
                            this timesheet. Please take note of
                            Payroll and Billing implications.
                        </small>
                    ) : (
                        <FormText
                            style={{ marginBottom: '20px' }}
                        >
                            This action will reject the
                            timesheet from billing review and
                            remove all charges from payroll
                            review.
                        </FormText>
                    )}

                    <Textarea
                        label="Notes"
                        id="selectedRowRejectionNotes"
                        name="selectedRowRejectionNotes"
                        required
                        placeholder="Enter notes regarding the rejection."
                        maxLength="500"
                        rows="5"
                        error="Notes are required"
                    />
                </>
            ),
        });
    };

    render() {
        const {
            rowData,
            gridOptions,
            isRejectingTimesheet,
            isApproving,
            rowsSelected,
            errorResponse,
        } = this.state;

        const billing = 'Billing';

        const { tenantSettings } = this.context.tenant;

        if (this.state.loading || !tenantSettings) {
            return (
                <Progress />
            );
        }

        if (!tenantSettings.billingEnabled) return <NotAuthorizedPage />;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faTasks}
                        className="mr-2 text-muted"
                    />
                    <span>{billing}</span>
                    <span
                        style={{
                            float: 'right',
                            position: 'relative',
                            top: '-5px',
                        }}
                    >
                        <Button
                            size="sm"
                            style={{ marginRight: '10px' }}
                            color="danger"
                            onClick={() => this.showExportModal()}
                        >
                            Export
                        </Button>
                        <Button
                            size="sm"
                            color="success"
                            style={{ marginRight: '10px' }}
                            onClick={() => this.onAcceptButtonClick()}
                            disabled={
                                rowsSelected.length === 0
                                || isApproving
                            }
                        >
                            Approve Selected
                        </Button>
                        {!tenantSettings.enableEstimate && (
                            <Button
                                size="sm"
                                color="danger"
                                onClick={() => this.toggleRejectTimesheetModal()}
                                disabled={
                                    rowsSelected.length !== 1
                                    || isRejectingTimesheet
                                }
                            >
                                Reject Selected
                            </Button>
                        )}

                    </span>
                </PageHeading>

                <DataGridToolbar
                    entity="Billing"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                    serverExport={{
                        apiPath: ApiRoutes.billing.excelExport(),
                        filePrefix: 'BillingSearch',
                    }}
                    serverExportDisabled={
                        !!this.state.saving
                        || !!this.state.loading
                        || !!this.state.loadingData
                    }
                />
                {!!this.state.loadingReport && (
                    <CustomCircularProgress />
                )}

                {!this.state.loadingReport && (
                    <DataGrid
                        domLayout="normal"
                        rowData={rowData}
                        gridOptions={gridOptions}
                        gridStatus={this.state.gridStatus}
                    />
                )}
                <TimesheetDetails
                    ref={this.timesheetDetailsRef}
                    show={this.state.timesheetDetailsOpen}
                    toggleShow={(open) => this.setState({
                        timesheetDetailsOpen: open,
                    })}
                    onClose={this.onTimesheetDetailsClosed}
                    onAccept={this.onAcceptTimesheet}
                    onlyBillable
                    isBilling
                />

                <FlexRow>
                    <FormErrorResponseDisplay
                        onClear={this.clearSaveErrors}
                        response={errorResponse}
                    />
                </FlexRow>
            </PageWrap>
        );
    }
}

export default BillingIndex;
